import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, FileType, HardDrive, Shield, Network, AlertTriangle, Fingerprint } from 'lucide-react';
import type { SandboxAnalysis } from '@/lib/types/sandbox';

interface SandboxResultsProps {
  results: SandboxAnalysis[];
}

function getScoreColor(score: number): string {
  if (!score && score !== 0) return 'text-muted-foreground';
  if (score <= 20) return 'text-green-500';
  if (score <= 40) return 'text-yellow-500';
  if (score <= 60) return 'text-orange-500';
  if (score <= 80) return 'text-red-400';
  return 'text-red-500';
}

function formatNumber(num: number | undefined): string {
  if (typeof num !== 'number') return '0';
  return num.toLocaleString();
}

function DetailedAnalysis({ result }: { result: SandboxAnalysis }) {
  if (!result) return null;

  return (
    <Card>
      <CardHeader>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CardTitle className="text-xl">Sandbox Analysis Results</CardTitle>
            <Badge variant={result.verdict?.toLowerCase() === 'malicious' ? 'destructive' : 'default'}>
              {(result.verdict || 'UNKNOWN').toUpperCase()}
            </Badge>
          </div>
          {result.classification_tags?.length > 0 && (
            <div className="flex gap-2">
              {result.classification_tags.map(tag => (
                <Badge key={tag} variant="secondary">
                  {tag.toUpperCase()}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* File Information */}
        <div className="space-y-2 pb-6 border-b border-border">
          <h3 className="text-lg font-semibold">File Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-mono break-all">{result.submit_name}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Type</p>
              <p>{result.type}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Size</p>
              <p>{formatNumber(result.size)} bytes</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Environment</p>
              <p>{result.environment_description}</p>
            </div>
          </div>
        </div>

        {/* Threat Details */}
        <div className="space-y-2 pb-6 border-b border-border">
          <h3 className="text-lg font-semibold">Threat Details</h3>
          <div className="grid grid-cols-2 gap-4">
            {(typeof result.threat_score === 'number' || result.threat_score === 0) && (
              <div>
                <div className="flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Threat Score</p>
                </div>
                <p className={`text-xl font-bold ${getScoreColor(result.threat_score)}`}>
                  {result.threat_score}/100
                </p>
              </div>
            )}
            <div>
              <div className="flex items-center gap-1">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">AV Detection Rate</p>
              </div>
              <p className={`text-xl font-bold ${getScoreColor(result.av_detect || 0)}`}>
                {result.av_detect || 0}%
              </p>
            </div>
            {result.vx_family && (
              <div className="col-span-2">
                <div className="flex items-center gap-1">
                  <Fingerprint className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">Malware Family</p>
                </div>
                <p className="font-medium">{result.vx_family}</p>
              </div>
            )}
          </div>
        </div>

        {/* MITRE ATT&CK Techniques */}
        {result.mitre_attcks?.length > 0 && (
          <div className="space-y-2 pb-6 border-b border-border">
            <h3 className="text-lg font-semibold">MITRE ATT&CK Techniques</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {result.mitre_attcks.map((technique, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-muted rounded-md">
                  <div>
                    <p className="font-medium">{technique.technique}</p>
                    <p className="text-sm text-muted-foreground">{technique.tactic}</p>
                  </div>
                  <a
                    href={technique.attck_id_wiki}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary hover:text-primary/80"
                  >
                    {technique.attck_id}
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Network Activity */}
        {(result.domains?.length > 0 || result.hosts?.length > 0) && (
          <div className="space-y-2 pb-6 border-b border-border">
            <h3 className="text-lg font-semibold">Network Activity</h3>
            <div className="grid gap-4">
              {result.domains?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Network className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Domains Contacted</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {result.domains.map((domain) => (
                      <Badge key={domain} variant="destructive">{domain}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {result.hosts?.length > 0 && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <Network className="h-4 w-4 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">IP Addresses</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {result.hosts.map((host) => (
                      <Badge key={host} variant="destructive">{host}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signatures */}
        {result.signatures?.length > 0 && (
          <div className="space-y-2 pt-2">
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-background z-10 py-4 border-b">
              <h3 className="text-lg font-semibold">Detection Signatures</h3>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    result.signatures.forEach((_, index) => {
                      const element = document.getElementById(`sig-desc-${index}`);
                      if (element) {
                        element.classList.remove('hidden');
                      }
                    });
                  }}
                >
                  Expand All
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    result.signatures.forEach((_, index) => {
                      const element = document.getElementById(`sig-desc-${index}`);
                      if (element) {
                        element.classList.add('hidden');
                      }
                    });
                  }}
                >
                  Collapse All
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {result.signatures
                .sort((a, b) => (b.threat_level || 0) - (a.threat_level || 0))
                .map((sig, index) => (
                  <div
                    key={`${sig.name}-${sig.threat_level}-${index}`}
                    className="relative flex cursor-default select-none items-center rounded-sm p-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 bg-muted"
                    onClick={() => {
                      const element = document.getElementById(`sig-desc-${index}`);
                      if (element) {
                        element.classList.toggle('hidden');
                      }
                    }}
                  >
                    <div className="flex flex-col flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-base font-medium">{sig.name}</span>
                        <Badge 
                          variant={
                            sig.threat_level_human === 'malicious' ? 'destructive' :
                            sig.threat_level_human === 'suspicious' ? 'warning' :
                            'primary'
                          }
                          className="ml-2"
                        >
                          {sig.threat_level_human}
                        </Badge>
                      </div>
                      <div id={`sig-desc-${index}`} className="hidden space-y-2">
                        <p className="text-sm text-muted-foreground">{sig.description}</p>
                        {sig.attck_id && (
                          <a
                            href={sig.attck_id_wiki || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            MITRE ATT&CK: {sig.attck_id}
                            <ExternalLink className="ml-1 h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SandboxResults({ results }: SandboxResultsProps) {
  const [selectedResult, setSelectedResult] = useState<SandboxAnalysis | null>(null);

  const handleResultSelect = useCallback((result: SandboxAnalysis) => {
    setSelectedResult(result);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedResult(null);
  }, []);

  // Early return if no results
  if (!results?.length) {
    return null;
  }

  return (
    <div className="space-y-6">
      {selectedResult ? (
        <>
          <Button variant="ghost" onClick={handleBack} className="mb-4">
            ← Back to Results
          </Button>
          <DetailedAnalysis result={selectedResult} />
        </>
      ) : results.length > 1 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {results.map((result) => (
            <Card 
              key={`${result.sha256}-${result.job_id}`}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => handleResultSelect(result)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg break-all line-clamp-2">{result.submit_name}</CardTitle>
                  <Badge variant={result.verdict?.toLowerCase() === 'malicious' ? 'destructive' : 'default'}>
                    {(result.verdict || 'UNKNOWN').toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="flex items-center gap-1">
                      <FileType className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Type</p>
                    </div>
                    <p className="truncate">{result.type}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <HardDrive className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Size</p>
                    </div>
                    <p>{formatNumber(result.size)} bytes</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">Environment</p>
                    </div>
                    <p className="truncate">{result.environment_description}</p>
                  </div>
                  {(typeof result.threat_score === 'number' || result.threat_score === 0) && (
                    <div>
                      <div className="flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Score</p>
                      </div>
                      <p className={getScoreColor(result.threat_score)}>{result.threat_score}/100</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <DetailedAnalysis result={results[0]} />
      )}
    </div>
  );
}

export { SandboxResults };